import { motion, useInView } from "framer-motion";
import { useRef, useState } from "react";
import { Play, ArrowRight, ShieldCheck, CreditCard } from "lucide-react";
import { Button } from "./ui/button";
import { VideoModal } from "./VideoModal";
import logoWebp320 from "@/assets/logo-320.webp";
import logoWebp640 from "@/assets/logo-640.webp";
import logoPng320 from "@/assets/logo-320.png";
import slackUrl from "@/assets/slack.svg";
import teamsUrl from "@/assets/teams.svg";
import gmailUrl from "@/assets/gmail.svg";
import outlookUrl from "@/assets/outlook.svg";
import googleCalendarUrl from "@/assets/google-calendar.png";

/* ─── Google OAuth logo (coloured) ─── */
const GoogleLogo = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" aria-hidden="true" focusable="false" className="flex-shrink-0">
    <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/>
    <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
    <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l3.66-2.84z" fill="#FBBC05"/>
    <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
  </svg>
);

/* ─── Platform inline SVGs ─── */
const InstagramSVG = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="#C13584" aria-label="Instagram">
    <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
  </svg>
);

const FacebookSVG = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="#1877F2" aria-label="Facebook">
    <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
  </svg>
);

const GoogleWorkspaceSVG = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" aria-label="Google Workspace">
    <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/>
    <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
    <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l3.66-2.84z" fill="#FBBC05"/>
    <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
  </svg>
);

/* ─── Unified Platforms Array ─── */
const platformLogos = [
  { label: "Slack",            node: <img src={slackUrl}           alt="Slack"           className="h-7 w-auto object-contain" /> },
  { label: "Microsoft Teams",  node: <img src={teamsUrl}           alt="Teams"           className="h-7 w-auto object-contain" /> },
  { label: "Gmail",            node: <img src={gmailUrl}           alt="Gmail"           className="h-7 w-auto object-contain" /> },
  { label: "Outlook",          node: <img src={outlookUrl}         alt="Outlook"         className="h-7 w-auto object-contain" /> },
  { label: "Google Calendar",  node: <img src={googleCalendarUrl} alt="Google Calendar" className="h-7 w-auto object-contain" /> },
  { label: "Google Workspace", node: <GoogleWorkspaceSVG /> },
  { label: "Facebook",         node: <FacebookSVG /> },
  { label: "Instagram",        node: <InstagramSVG /> },
];

export const HeroSection = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true });
  const [isVideoOpen, setIsVideoOpen] = useState(false);

  return (
    <section id="home" className="relative min-h-screen flex items-center pt-20 overflow-hidden">
      {/* Background */}
      <div className="absolute inset-0 noise-texture" />

      {/* ── Floating Logo: Vertical Position adjusted to align with CTA buttons ── */}
      <div className="hidden lg:flex absolute top-[52%] -translate-y-1/2 right-0 w-[600px] h-[600px] pointer-events-none items-center justify-center">
        <motion.div
          className="relative"
          animate={{ y: [0, -20, 0] }}
          transition={{ duration: 8, repeat: Infinity, ease: "easeInOut" }}
        >
          <div className="absolute inset-0 w-full h-full bg-gradient-to-br from-primary/20 to-accent/20 rounded-full blur-3xl scale-150" />
          <motion.div
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 1, delay: 0.3 }}
          >
            <picture>
              <source srcSet={`${logoWebp320} 320w, ${logoWebp640} 640w`} sizes="320px" type="image/webp" />
              <img
                src={logoPng320}
                alt="ClearMakes Logo"
                width={320}
                height={320}
                className="relative w-80 h-80 object-contain drop-shadow-2xl"
              />
            </picture>
          </motion.div>
        </motion.div>
      </div>

      <div className="container relative z-10" ref={ref}>
        <div className="max-w-3xl">

          {/* ── Heading ── */}
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.8, ease: "easeOut" }}
          >
            <h1 className="heading-xl mb-2">
              <span className="gradient-text-primary">ClearMakes</span>
            </h1>
            <h2 className="heading-lg text-foreground/80 mb-6">
              Enterprise Communication Unified
            </h2>
          </motion.div>

          {/* ── Sub-heading ── */}
          <motion.p
            className="body-lg max-w-xl mb-8"
            initial={{ opacity: 0, y: 40 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.8, delay: 0.2, ease: "easeOut" }}
          >
            One secure platform to manage all your business messaging. We centralize your data and automate workflows so your team can focus on execution.
          </motion.p>

          {/* ── CTA Buttons ── */}
          <motion.div
            className="flex flex-col gap-3 sm:gap-4"
            initial={{ opacity: 0, y: 40 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.8, delay: 0.4, ease: "easeOut" }}
          >
            {/* Row 1: Google + Email */}
            <div className="flex flex-row items-center gap-2 sm:gap-3">
              {/* Google OAuth */}
              <Button
                asChild
                className="h-10 px-3 text-xs sm:h-12 sm:px-6 sm:text-base bg-white hover:bg-gray-50 text-gray-800 font-semibold shadow-lg shadow-black/20 hover:shadow-xl border border-gray-200/30 gap-2 transition-all duration-200 flex-shrink-0"
              >
                <a href="https://clearmakes.com/api/auth/google/login">
                  <GoogleLogo />
                  <span>Continue with Google</span>
                </a>
              </Button>

              {/* Email sign-up */}
              <Button
                variant="hero"
                asChild
                className="h-10 px-3 text-xs sm:h-12 sm:px-6 sm:text-base gap-1.5 sm:gap-2.5 group flex-shrink-0"
              >
                <a href="https://clearmakes.com/login?view=signup">
                  <span>Start Free with Email</span>
                  <ArrowRight size={14} className="transition-transform duration-200 group-hover:translate-x-0.5 sm:w-4 sm:h-4" />
                </a>
              </Button>
            </div>

            {/* Row 2: Watch Demo */}
            <div>
              <Button
                variant="glass"
                onClick={() => setIsVideoOpen(true)}
                className="h-10 px-4 text-xs sm:h-12 sm:px-6 sm:text-base group gap-2 w-fit"
              >
                <div className="relative flex items-center justify-center w-6 h-6 sm:w-7 sm:h-7 rounded-full bg-primary/20 group-hover:bg-primary/30 transition-colors">
                  <Play size={12} className="ml-0.5 text-primary sm:w-3.5 sm:h-3.5" fill="currentColor" />
                </div>
                <span>Watch Demo</span>
              </Button>
            </div>
          </motion.div>

          {/* ── Compliance Strip & Payment info ── */}
          <motion.div
            className="flex flex-col gap-2 mt-6"
            initial={{ opacity: 0 }}
            animate={isInView ? { opacity: 1 } : {}}
            transition={{ duration: 0.7, delay: 0.6 }}
          >
            {/* Badges */}
            <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
              <span className="flex items-center gap-1.5 text-xs text-gray-400 font-medium">
                <ShieldCheck size={14} /> GDPR Compliant
              </span>
              <span className="flex items-center gap-1.5 text-xs text-gray-400 font-medium">
                <ShieldCheck size={14} /> SOC 2 Type II
              </span>
            </div>
            {/* No Credit Card Required */}
            <div className="flex items-center gap-1.5 text-xs text-gray-400 font-medium">
              <CreditCard size={14} /> No credit card required
            </div>
          </motion.div>

          {/* ── Trusted by Platform Logos ── */}
          <motion.div
            className="mt-10 py-8 border-t border-b border-white/10"
            initial={{ opacity: 0, y: 10 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.7, delay: 0.75 }}
          >
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-gray-500 mb-8 sm:mb-10 text-left">
              Official Integration Partners
            </p>

            <div className="grid grid-cols-4 lg:grid-cols-8 gap-y-8 gap-x-4 sm:gap-x-6 w-full place-items-center">
              {platformLogos.map(({ label, node }, i) => (
                <div key={i} className="flex flex-col items-center justify-between group cursor-pointer h-[55px] w-full">
                  <div className="flex-1 flex items-center justify-center grayscale opacity-50 group-hover:grayscale-0 group-hover:opacity-100 transition-all duration-300">
                    {node}
                  </div>
                  <span className="text-[10px] whitespace-nowrap mt-3 text-gray-500 font-medium group-hover:text-gray-300 transition-colors text-center">
                    {label}
                  </span>
                </div>
              ))}
            </div>
          </motion.div>

        </div>
      </div>

      {/* Bottom gradient fade */}
      <div className="absolute bottom-0 left-0 right-0 h-32 bg-gradient-to-t from-background to-transparent pointer-events-none" />

      <VideoModal
        isOpen={isVideoOpen}
        onClose={() => setIsVideoOpen(false)}
        videoUrl="https://youtu.be/pl14WtOCIhM"
      />
    </section>
  );
};